import { useEffect, useRef, useState } from 'react'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingAnimation from 'components/LoadingAnimation'
import { Plus } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardBody, CardTitle, Col, Row } from 'reactstrap'
import { addItem } from '../sellCommoditySlice'
import {
  getComodities,
  getInventories,
  getPackings,
} from '../sellCommodityAction'
import Item2 from './index2'

const ItemInfo = ({ invToTrx = false, isInventory, setIsInventory }) => {
  const dispatch = useDispatch()
  const {
    addItemCount,
    orders,
    comodities,
    inventories,
    packings,
    isLoading,
  } = useSelector((state) => state.sellCommodity)

  const [loadingItems, setLoadingItems] = useState({})

  // Calculate resolution progress for inventory preparation
  const totalToResolve = addItemCount?.length || 0
  const itemsFinished = Object.values(loadingItems).filter(
    (val) => val === false,
  ).length
  const resolutionPercentage =
    totalToResolve > 0 ? Math.round((itemsFinished / totalToResolve) * 100) : 0

  // For ManageStock flow (invToTrx), we wait specifically for individual item resolution.
  // For normal flow, we follow global loading state.
  const isAnyItemLoading = invToTrx
    ? itemsFinished < totalToResolve
    : Object.values(loadingItems).some(Boolean) ||
      (isLoading && (!comodities || !inventories || !packings))

  const setItemLoading = (fieldNum, isLoading) => {
    setLoadingItems((prev) => ({ ...prev, [fieldNum]: isLoading }))
  }

  const promiseRefs = useRef({
    comodities: null,
    inventories: null,
    packings: null,
  })

  const itemRefs = useRef([])
  const [shouldFocus, setShouldFocus] = useState(false)

  useEffect(() => {
    // Always trigger refresh on mount for "newest data and clean"
    // The slice logic now ensures isLoading only becomes true if data is empty.

    if (!invToTrx) {
      if (promiseRefs.current.comodities) promiseRefs.current.comodities.abort()
      promiseRefs.current.comodities = dispatch(getComodities())
    }

    if (promiseRefs.current.inventories) promiseRefs.current.inventories.abort()
    promiseRefs.current.inventories = dispatch(getInventories())

    if (promiseRefs.current.packings) promiseRefs.current.packings.abort()
    promiseRefs.current.packings = dispatch(getPackings())

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.values(promiseRefs.current).forEach((p) => p?.abort())
    }
  }, [invToTrx, dispatch])

  useEffect(() => {
    if (shouldFocus && itemRefs.current.length > 0) {
      const emptyIndex = addItemCount.findIndex(
        (item, idx) => !itemRefs.current[idx]?.value,
      )

      if (emptyIndex !== -1) {
        itemRefs.current[emptyIndex]?.focus()
      } else {
        const lastIndex = itemRefs.current.length - 1
        itemRefs.current[lastIndex]?.focus()
      }

      setShouldFocus(false)
    }
  }, [shouldFocus, addItemCount])

  const renderLoader = () => {
    // Circular progress math
    const radius = 28
    const circumference = 2 * Math.PI * radius
    const offset = circumference - (resolutionPercentage / 100) * circumference

    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center p-4 rounded mb-3"
        style={{
          backgroundColor: '#fcfcfd',
          minHeight: '160px',
          border: '1px dashed #EAECF0',
        }}
      >
        <div
          className="position-relative mb-3"
          style={{ width: '80px', height: '80px' }}
        >
          <svg className="w-100 h-100" viewBox="0 0 100 100">
            <circle
              strokeWidth="5"
              stroke="#F2F4F7"
              fill="transparent"
              r={radius}
              cx="50"
              cy="50"
            />
            <circle
              strokeWidth="5"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              stroke="#005270"
              fill="transparent"
              r={radius}
              cx="50"
              cy="50"
              style={{
                transition: 'stroke-dashoffset 0.5s ease',
                transform: 'rotate(-90deg)',
                transformOrigin: '50% 50%',
              }}
            />
          </svg>
          <div
            className="position-absolute top-50 start-50 translate-middle fw-bold"
            style={{ fontSize: '0.9rem', color: '#005270' }}
          >
            {resolutionPercentage}%
          </div>
        </div>
        <span
          className="fw-bold"
          style={{ fontSize: '1rem', color: '#101828' }}
        >
          Menyiapkan Inventori
        </span>
        <span className="text-muted small">
          Mohon tunggu, sedang memproses data...
        </span>
      </div>
    )
  }

  return invToTrx ? (
    <div>
      <Row>
        <Col sm="12">
          {isAnyItemLoading && renderLoader()}
          <div style={{ display: isAnyItemLoading ? 'none' : 'block' }}>
            {addItemCount?.map((num, idx) => (
              <div key={idx} data-testid={`sell-form-item-row-${idx}`}>
                <Item2
                  key={idx}
                  fieldNum={num}
                  idx={idx}
                  refItem={(el) => (itemRefs.current[idx] = el)}
                  invToTrx
                  isInventory={isInventory}
                  setIsInventory={setIsInventory}
                  setItemLoading={setItemLoading}
                />
              </div>
            ))}

            <CustomButton
              size="sm"
              className="mt-1"
              style={{ maxWidth: 186 }}
              outline
              color="primary"
              testId="sell-form-add-item-button"
              onClick={() => {
                dispatch(addItem())
                // setShouldFocus(true)
              }}
            >
              <Plus size={16} /> Tambah Barang
            </CustomButton>
          </div>
        </Col>
      </Row>
    </div>
  ) : (
    <Card className="border-0 shadow-sm">
      <CardBody className="p-4">
        <Row>
          <Col sm="12">
            <CardTitle tag="h5" className="mb-3">
              Barang yang dijual
            </CardTitle>
            {isAnyItemLoading && renderLoader()}
            <div style={{ display: isAnyItemLoading ? 'none' : 'block' }}>
              {addItemCount?.map((num, idx) => (
                <div key={idx} data-testid={`sell-form-item-row-${idx}`}>
                  <Item2
                    key={idx}
                    fieldNum={num}
                    idx={idx}
                    refItem={(el) => (itemRefs.current[idx] = el)}
                    isInventory={isInventory}
                    setIsInventory={setIsInventory}
                    setItemLoading={setItemLoading}
                  />
                </div>
              ))}

              <CustomButton
                className="mt-2"
                size="sm"
                style={{ maxWidth: 186 }}
                outline
                color="primary"
                testId="sell-form-add-item-button"
                onClick={() => {
                  dispatch(addItem())
                  // setShouldFocus(true)
                }}
              >
                <Plus size={16} /> Tambah Barang
              </CustomButton>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

export default ItemInfo
